import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../components/Toast';
import { Card } from '../../components/ui';
import Button from '../../components/Button';

export default function Branding() {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [primary, setPrimary] = useState('#5B21FF');
  const [primaryDark, setPrimaryDark] = useState('#26148C');
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/branding').then(res => {
      setName(res.data.name ?? '');
      const b = res.data.branding ?? {};
      if (b.primary) setPrimary(b.primary);
      if (b.primaryDark) setPrimaryDark(b.primaryDark);
      if (b.logoUrl) setLogoUrl(b.logoUrl);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Live-preview the colour on the running app.
  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary', primary);
    document.documentElement.style.setProperty('--brand-primary-dark', primaryDark);
  }, [primary, primaryDark]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/branding', { name, branding: { primary, primaryDark, logoUrl } });
      toast('Branding saved', 'success');
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-5 text-sm text-gray-400">Loading…</div>;

  return (
    <div className="p-5 space-y-4 max-w-2xl">
      <div>
        <h1 className="text-xl font-bold text-navy">Branding</h1>
        <p className="text-sm text-gray-500">Customise how the portal looks for your organisation.</p>
      </div>

      <Card>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Organisation name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Primary colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={primary} onChange={e => setPrimary(e.target.value)} className="w-10 h-9 rounded border border-gray-200" />
                <input value={primary} onChange={e => setPrimary(e.target.value)}
                  className="flex-1 text-sm font-mono rounded-lg border border-gray-300 px-3 py-2" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Primary (dark)</label>
              <div className="flex items-center gap-2">
                <input type="color" value={primaryDark} onChange={e => setPrimaryDark(e.target.value)} className="w-10 h-9 rounded border border-gray-200" />
                <input value={primaryDark} onChange={e => setPrimaryDark(e.target.value)}
                  className="flex-1 text-sm font-mono rounded-lg border border-gray-300 px-3 py-2" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Logo URL (optional)</label>
            <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://…"
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand" />
          </div>

          {/* Preview */}
          <div className="pt-2">
            <p className="text-xs text-gray-400 mb-2">Preview</p>
            <div className="flex items-center gap-3 p-4 rounded-xl border border-gray-200">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-extrabold text-white"
                   style={{ background: primary }}>{(name || 'S').charAt(0)}</div>
              <span className="font-brand font-extrabold text-navy">{name || 'Your Org'}</span>
              <button className="ml-auto text-sm font-semibold text-white rounded-lg px-3 py-2" style={{ background: primary }}>
                Primary button
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save branding'}</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
